// MorrowOS Organization surface — the company-level workspace, distinct from the
// personal Knowledge base and from the Team roster. It holds the organizational
// memory today; the hierarchy of people, their working styles, manager-level
// "how are my workers doing" queries, and pluggable AI workers/agents land here
// next (see the roadmap in the org-block plan).
import { OrganizationIcon, RefreshCwIcon, RotateCwIcon } from "lucide-react";
import { useOrganization } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { useI18n } from "~/i18n";
import { resolveComposioUserId } from "../../composioConnect";
import { resolveOrganizationLogoUrl } from "../onboarding/MorrowOnboarding.logic";
import { useOnboardingProfile } from "../../onboardingStore";
import {
  fetchOrgKnowledgeDoc,
  knowledgeErrorMessageKey,
  rebuildOrgKnowledge,
  type KnowledgeErrorCode,
  type KnowledgeProbeState,
} from "../../morrowKnowledge";
import ChatMarkdown from "../ChatMarkdown";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { MorrowDocumentSkeleton } from "../morrow/MorrowPageSkeletons";
import { MorrowDocumentColumn } from "./MorrowDocumentColumn";
import { MorrowOrganizationIdentityCard } from "./MorrowIdentityCard";
import { MorrowWorkspacePage } from "./MorrowWorkspacePage";
import { MorrowMemoryToggleButton } from "../memory/MorrowMemoryToggleButton";
import { MorrowScopedMemoryPanel } from "../memory/MorrowMemoryPage";
/**
 * Resolves the active Clerk org + whether the caller is an org admin, then renders
 * the body. `useOrganization` needs a ClerkProvider, so non-Clerk desktop/local
 * builds (no org concept) render an empty body directly.
 */
export function MorrowOrganizationPage() {
  if (hasClerkPublishableKey()) return <ClerkMorrowOrganizationPage />;
  return <MorrowOrganizationPageBody orgId={null} orgName={null} isAdmin={false} />;
}

function ClerkMorrowOrganizationPage() {
  const { organization, membership } = useOrganization();
  const companyLogoFallback = useOnboardingProfile().companyLogoUrl;
  const memberCount = organization?.membersCount;
  return (
    <MorrowOrganizationPageBody
      orgId={organization?.id ?? null}
      orgName={organization?.name ?? null}
      {...(typeof memberCount === "number" ? { memberCount } : {})}
      logoUrl={organization ? resolveOrganizationLogoUrl(organization, companyLogoFallback) : ""}
      isAdmin={membership?.role === "org:admin"}
    />
  );
}

function MorrowOrganizationPageBody({
  orgId,
  orgName,
  memberCount,
  logoUrl = "",
  isAdmin,
}: {
  readonly orgId: string | null;
  readonly orgName: string | null;
  readonly memberCount?: number;
  readonly logoUrl?: string;
  readonly isAdmin: boolean;
}) {
  const { t } = useI18n();
  const navigate = useNavigate({ from: "/organization" });
  const { view: viewFromSearch } = useSearch({ strict: false });
  const showMemory = viewFromSearch === "memory";
  const onboardingProfile = useOnboardingProfile();
  const { isLoaded: clerkLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  const userId = resolveComposioUserId(clerkLoaded && isSignedIn ? clerkUserId : null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<KnowledgeErrorCode | null>(null);
  const [loading, setLoading] = useState(Boolean(orgId));
  const [rebuilding, setRebuilding] = useState(false);
  const [probeState, setProbeState] = useState<KnowledgeProbeState>("idle");

  const load = useCallback(
    async (quiet = false) => {
      if (!orgId) return;
      if (!quiet) setLoading(true);
      const doc = await fetchOrgKnowledgeDoc(orgId);
      setMarkdown(doc.markdown);
      setError(doc.error);
      setErrorCode(doc.errorCode);
      setProbeState(doc.probeState);
      if (!quiet) setLoading(false);
    },
    [orgId],
  );

  useEffect(() => {
    if (!orgId) return;
    void load();
  }, [load, orgId]);

  useEffect(() => {
    if (probeState !== "running") return;
    const timer = setInterval(() => void load(true), 5000);
    return () => clearInterval(timer);
  }, [probeState, load]);

  const rebuild = useCallback(async () => {
    if (!userId || !orgId) return;
    setRebuilding(true);
    const started = await rebuildOrgKnowledge(userId, orgId);
    setRebuilding(false);
    if (started) {
      setProbeState("running");
      void load(true);
    }
  }, [userId, orgId, load]);

  const busy = rebuilding || probeState === "running";

  const toggleMemoryView = useCallback(() => {
    void navigate({
      search: (current) => {
        if (showMemory) {
          const { view: _view, ...rest } = current as { view?: string };
          return rest;
        }
        return { ...current, view: "memory" as const };
      },
      replace: true,
    });
  }, [navigate, showMemory]);

  return (
    <MorrowWorkspacePage
      title={orgName ?? t("organization.title")}
      subtitle={showMemory ? t("memory.subtitleOrg") : t("organization.subtitle")}
      icon={<OrganizationIcon />}
      wide
      action={
        orgId ? (
          <div className="flex items-center gap-2">
            <MorrowMemoryToggleButton active={showMemory} onClick={toggleMemoryView} />
            {showMemory ? null : (
              <>
                {isAdmin ? (
                  <Button type="button" size="sm" onClick={() => void rebuild()} disabled={busy}>
                    {busy ? <Spinner /> : <RotateCwIcon aria-hidden="true" />}
                    {busy ? t("team.orgMemoryRebuilding") : t("team.orgMemoryRebuild")}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => void load()}
                  disabled={loading}
                  aria-label={loading ? t("common.loading") : t("common.refresh")}
                  title={loading ? t("common.loading") : t("common.refresh")}
                >
                  <RefreshCwIcon className={loading ? "animate-spin" : ""} aria-hidden="true" />
                </Button>
              </>
            )}
          </div>
        ) : null
      }
    >
      <MorrowDocumentColumn
        aside={
          orgId ? (
            <MorrowOrganizationIdentityCard
              name={orgName ?? t("organization.title")}
              logoUrl={logoUrl}
              {...(typeof memberCount === "number" ? { memberCount } : {})}
              industry={onboardingProfile.questionnaire.industry ?? ""}
              website={onboardingProfile.companyWebsite}
              teamSize={onboardingProfile.questionnaire.teamSize ?? ""}
              goal={onboardingProfile.questionnaire.goal ?? ""}
              about={onboardingProfile.companyBrief}
              description={t("identity.orgHint")}
            />
          ) : undefined
        }
      >
        {orgId ? (
          showMemory ? (
            <MorrowScopedMemoryPanel scope="org" orgName={orgName ?? t("organization.title")} />
          ) : (
            <OrgMemoryContent
              loading={loading}
              error={error}
              errorCode={errorCode}
              markdown={markdown}
              busy={busy}
            />
          )
        ) : (
          <div className="flex flex-col items-start gap-2 py-16">
            <p className="text-sm text-muted-foreground">{t("organization.noOrg")}</p>
          </div>
        )}
      </MorrowDocumentColumn>
    </MorrowWorkspacePage>
  );
}

function OrgMemoryContent({
  loading,
  error,
  errorCode,
  markdown,
  busy,
}: {
  readonly loading: boolean;
  readonly error: string | null;
  readonly errorCode: KnowledgeErrorCode | null;
  readonly markdown: string | null;
  readonly busy: boolean;
}) {
  const { t } = useI18n();

  if (loading) {
    return <MorrowDocumentSkeleton />;
  }

  if (errorCode) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-foreground">
        {t(knowledgeErrorMessageKey(errorCode))}
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-muted-foreground">{error}</p>;
  }

  if (markdown) {
    return <ChatMarkdown text={markdown} cwd={undefined} documentSurface />;
  }

  if (busy) {
    return <p className="text-sm text-muted-foreground">{t("team.orgMemoryRebuilding")}</p>;
  }

  return <p className="text-sm text-muted-foreground">{t("team.orgMemoryEmpty")}</p>;
}
